import React from "react";
import { useTranslation } from "react-i18next";
import Header from "./Header";
import OnlineSidebar from "./OnlineSidebar";
import EmailVerificationBanner from "./EmailVerificationBanner";
import ShadowbanBanner from "./ShadowbanBanner";
import MobileShell, { useIsMobile } from "./MobileShell";
import { useAuth } from "../contexts/AuthContext";
import { Link } from "react-router-dom";
import BetaSidebar from "./beta/BetaSidebar";

export default function Layout({ children, withSidebar = true, hideFooter = false, hideMain = false, wide = false, noNav = false }) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { user } = useAuth();
  // Mobil-UI åpen for alle innloggede brukere (fjernet owner-lock 22.07.2026).
  const useMobileShell = isMobile && !!user;
  const showNav = !!user && !noNav;

  // Mobil: bruk MobileShell (bunn-nav + kompakt header, ingen viking-mønster/footer)
  if (useMobileShell) {
    return (
      <MobileShell>
        <EmailVerificationBanner />
        <ShadowbanBanner />
        <div className="px-3 py-3">{children}</div>
      </MobileShell>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative">
      <Header />
      <EmailVerificationBanner />
      <ShadowbanBanner />
      <div className="flex-1 w-full flex relative z-10">
        {showNav && (
          <div className="hidden lg:block w-56 shrink-0 pl-4 pt-6 sm:pt-10 lg:pt-14 [&>aside]:top-[calc(var(--beta-banner-h,0px)+6rem)]" data-testid="layout-left-nav">
            <BetaSidebar />
          </div>
        )}
        <main className={`flex-1 min-w-0 w-full mx-auto ${showNav ? "has-left-nav" : ""} ${wide ? "" : "max-w-7xl"} px-3 sm:px-6 lg:px-8 py-6 sm:py-10 lg:py-14`}>
          <div className="flex gap-6 lg:gap-12">
            <div className="flex-1 min-w-0">{children}</div>
            {withSidebar && <OnlineSidebar />}
          </div>
        </main>
      </div>
      {!hideFooter && (
      <footer
        className="relative z-10 mt-8"
        style={{
          background: "var(--noir-2)",
          borderTop: "1px solid rgba(212, 165, 116, 0.7)",
          borderBottom: "1px solid rgba(212, 165, 116, 0.25)",
        }}
      >
        <div className="w-full px-4 sm:px-6 lg:px-8 xl:px-10 py-3 flex flex-wrap items-center gap-x-5 gap-y-2">
          <span className="brand-text text-lg shrink-0 mr-3">Norsk Erotikk</span>
          <span className="font-meta text-[var(--cream-soft)] whitespace-nowrap">{t("footer.tagline")} · {t("footer.country")}</span>
          <Link to="/veyl?utm_source=footer&utm_campaign=veyl_launch" data-testid="footer-veyl" className="font-meta hover:text-[var(--cream)] whitespace-nowrap" style={{ color: "#c2185b" }}>Veyl — trygg sladding</Link>
          <Link to="/bli-onlyfans-skaper" data-testid="footer-of-ad" className="font-meta hover:text-[var(--cream)] whitespace-nowrap" style={{ color: "#5BA5C9" }}>{t("footer.become_creator")}</Link>
          <Link to="/dmca" className="font-meta hover:text-[var(--cream)] whitespace-nowrap">DMCA</Link>
          <Link to="/vilkar" data-testid="footer-vilkar" className="font-meta hover:text-[var(--cream)] whitespace-nowrap">{t("footer.terms")}</Link>
          <Link to="/personvern" data-testid="footer-personvern" className="font-meta hover:text-[var(--cream)] whitespace-nowrap">{t("footer.privacy")}</Link>
          <Link to="/cookies" data-testid="footer-cookies" className="font-meta hover:text-[var(--cream)] whitespace-nowrap">{t("footer.cookies")}</Link>
          <Link to="/husregler" data-testid="footer-husregler" className="font-meta hover:text-[var(--cream)] whitespace-nowrap">{t("footer.house_rules")}</Link>
          <Link to="/kontakt" data-testid="footer-kontakt" className="font-meta hover:text-[var(--cream)] whitespace-nowrap">{t("footer.contact")}</Link>
          <span className="font-meta ml-auto whitespace-nowrap text-[var(--cream-faint)]">{t("footer.copyright", { year: new Date().getFullYear() })}</span>
        </div>
      </footer>
      )}
    </div>
  );
}
